import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ONBOARDING_COMPLETE_KEY = 'naewaste_onboarding_complete';
const ONBOARDING_STEP_KEY = 'naewaste_onboarding_step';

const onboardingSteps = [
  {
    id: 'welcome',
    title: 'Welcome to NaeWaste!',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          NaeWaste helps you reduce food waste by tracking your items, managing expiry dates, and logging waste.
        </Typography>
        <Typography variant="body1" paragraph>
          This quick tour will show you the main features. You can skip it anytime or restart it from your profile.
        </Typography>
      </Box>
    ),
    route: '/app/items',
  },
  {
    id: 'items',
    title: 'Track Your Food Items',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>Add Items:</strong> Click &quot;Add Item&quot; to manually enter food items, or use the barcode scanner to automatically get product information.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Set Expiry Dates:</strong> Always set an expiry date so you get notified before items expire.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Organize by Location:</strong> Use locations (Fridge, Freezer, Pantry) to find items quickly.
        </Typography>
      </Box>
    ),
    route: '/app/items',
  },
  {
    id: 'barcode',
    title: 'Barcode Scanner',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>Quick Product Lookup:</strong> Click the barcode scanner icon to scan product barcodes. The app will automatically fill in product name and category from the Open Food Facts database.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Camera Access:</strong> Make sure to allow camera permissions when prompted.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Manual Entry:</strong> If a product isn&apos;t found, you can always add it manually.
        </Typography>
      </Box>
    ),
    route: '/app/items',
  },
  {
    id: 'waste',
    title: 'Log Waste & Track Progress',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>Log Waste:</strong> When items go to waste, log them on the Waste page with a reason (expired, spoiled, etc.). This helps you understand your waste patterns.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Analytics:</strong> View your waste statistics, trends, and most wasted items to identify areas for improvement.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Consume Items:</strong> Don&apos;t forget to mark items as &quot;Consumed&quot; when you use them successfully!
        </Typography>
      </Box>
    ),
    route: '/app/waste',
  },
  {
    id: 'notifications',
    title: 'Expiry Notifications',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>Automatic Alerts:</strong> The app will notify you when items are about to expire (1-3 days before) or have already expired.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Notification Bell:</strong> Check the notification bell in the top toolbar to see all your alerts.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Settings:</strong> Manage notification preferences in your Profile settings.
        </Typography>
      </Box>
    ),
    route: '/app/items',
  },
  {
    id: 'community',
    title: 'Join the Community',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>Share Tips:</strong> Post tips, recipes, and experiences on the Community page.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Engage:</strong> Comment on posts, ask questions, and learn from other users.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Top Contributors:</strong> Active posters appear on the community contributor list.
        </Typography>
      </Box>
    ),
    route: '/app/community',
  },
  {
    id: 'profile',
    title: 'Your Profile & Settings',
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          <strong>Update Profile:</strong> Add a profile picture, update your display name, and customize your settings.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Data Management:</strong> Export your data or delete your account anytime from your Profile page.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Cookie Settings:</strong> Manage your cookie preferences from your Profile.
        </Typography>
      </Box>
    ),
    route: '/app/profile',
  },
  {
    id: 'complete',
    title: "You're All Set!",
    content: (
      <Box>
        <Typography variant="body1" paragraph>
          You now know the basics of NaeWaste! Start by adding a few items to track.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Need Help?</strong> Visit the Help & FAQ page anytime from the footer or user menu.
        </Typography>
        <Typography variant="body1" paragraph>
          Happy waste reducing! 🌱
        </Typography>
      </Box>
    ),
    route: '/app/items',
  },
];

export default function OnboardingTour({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Load saved step if tour was interrupted
    const savedStep = localStorage.getItem(ONBOARDING_STEP_KEY);
    if (savedStep && open) {
      const stepIndex = onboardingSteps.findIndex((step) => step.id === savedStep);
      if (stepIndex !== -1) {
        setActiveStep(stepIndex);
      }
    }
  }, [open]);

  const handleNext = () => {
    const nextStep = activeStep + 1;
    if (nextStep < onboardingSteps.length) {
      setActiveStep(nextStep);
      localStorage.setItem(ONBOARDING_STEP_KEY, onboardingSteps[nextStep].id);
      
      // Navigate to the step's route if it's different
      const currentRoute = onboardingSteps[activeStep].route;
      const nextRoute = onboardingSteps[nextStep].route;
      if (currentRoute !== nextRoute) {
        navigate(nextRoute);
      }
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      localStorage.setItem(ONBOARDING_STEP_KEY, onboardingSteps[prevStep].id);
      
      // Navigate to the step's route if it's different
      const currentRoute = onboardingSteps[activeStep].route;
      const prevRoute = onboardingSteps[prevStep].route;
      if (currentRoute !== prevRoute) {
        navigate(prevRoute);
      }
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    localStorage.removeItem(ONBOARDING_STEP_KEY);
    if (onClose) {
      onClose();
    }
  };

  if (!open) return null;

  const isLastStep = activeStep === onboardingSteps.length - 1;
  const currentStep = onboardingSteps[activeStep];

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleSkip}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ p: 3, pt: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600} color="primary">
            {currentStep.title}
          </Typography>

          <Box sx={{ my: 3, minHeight: '150px' }}>
            {currentStep.content}
          </Box>

          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
            {onboardingSteps.map((step, index) => (
              <Step key={step.id} completed={index < activeStep}>
                <StepLabel>
                  {step.title}
                </StepLabel>
                {index === activeStep && (
                  <StepContent>
                    <Typography variant="caption" color="text.secondary">
                      Step {index + 1} of {onboardingSteps.length}
                    </Typography>
                  </StepContent>
                )}
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Button
              onClick={handleSkip}
              color="inherit"
            >
              Skip Tour
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={isLastStep ? <CheckCircleIcon /> : <ArrowForwardIcon />}
              >
                {isLastStep ? 'Get Started' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

OnboardingTour.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// Helper function to check if onboarding is complete
export const isOnboardingComplete = () => {
  return localStorage.getItem(ONBOARDING_COMPLETE_KEY) === 'true';
};

// Helper function to reset onboarding (for testing or user request)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
  localStorage.removeItem(ONBOARDING_STEP_KEY);
};

